// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import Checkbox, { CheckboxStyle } from "@insite/mobius/Checkbox";
import CheckboxGroup, { CheckboxGroupProps } from "@insite/mobius/CheckboxGroup";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Radio, { RadioPresentationProps, RadioStyle } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupPresentationProps } from "@insite/mobius/RadioGroup";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextArea, { TextAreaPresentationProps } from "@insite/mobius/TextArea";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField/TextField";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import { CreateAccountFormInfoCollectionModel } from "@keller/App/Services/CreateAccountService";
import {
    AccountFormState,
    createAccountOptionValues,
} from "@keller/App/Widgets/KscCreateAccount/CreateAccount.form.module";
import FileUpload from "@keller/MobiusOverrides/FileUpload";
import Question from "@keller/XDL/Icons/Question";
import { xScreen } from "@keller/XDL/Utilities";
import { CreateForm } from "@keller/XDL/Utilities/form";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    accountData?: CreateAccountFormInfoCollectionModel;
    accountForm?: CreateForm<AccountFormState>;
}

interface CreateAccountApplyPurchasableFormStyles {
    havingAccountGridItem?: GridItemProps;
    checkboxGroupStyle?: CheckboxGroupProps;
    radioGroupStyle?: RadioGroupPresentationProps;
    alignToExistingAccountRadio?: RadioPresentationProps;
    desiredRoleGridItem?: GridItemProps;
    desiredRoleInfoGridItem?: GridItemProps;
    desiredRoleSelect?: SelectPresentationProps;
    adminNameEmailGridItem?: GridItemProps;
    textField?: TextFieldPresentationProps;
    textArea?: TextAreaPresentationProps;
    cashAccountRadio?: RadioPresentationProps;
    existingAccountsGridItem?: GridItemProps;
    industryGridItem?: GridItemProps;
    companyNameGridItem?: GridItemProps;
    companyNameInfoGridItem?: GridItemProps;
    tooltip?: TooltipPresentationProps;
    addressGridItem?: GridItemProps;
    countriesGridItem?: GridItemProps;
    cityGridItem?: GridItemProps;
    stateGridItem?: GridItemProps;
    zipCodeGridItem?: GridItemProps;
    notesGridItem?: GridItemProps;
    stayInTouchGridItem?: GridItemProps;
    emptyGridItem?: GridItemProps;
    stayInTouchRadio?: RadioPresentationProps;
    uploadCreditApplicationSiteMessageGridItem?: GridItemProps;
    uploadCreditApplicationGridItem?: GridItemProps;
}

const createAccountApplyPurchasableFormStyles: CreateAccountApplyPurchasableFormStyles = {
    havingAccountGridItem: {
        width: 12,
    },
    desiredRoleGridItem: {
        width: [10, 10, 4, 4, 4],
        css: css`
            padding-top: 24px;
        `,
    },
    desiredRoleInfoGridItem: {
        width: [2, 2, 8, 8, 8],
        css: css`
            align-items: center;
            padding-top: 23px;
            &&& {
                margin-top: 16px;
            }
        `,
    },
    radioGroupStyle: {
        css: css`
            legend {
                font-size: 15px;
                font-weight: normal;
                letter-spacing: 0;
                line-height: 24px;
            }
            ${xScreen("lte", "xs")} {
                display: flex;
                flex-direction: column;
                & > ${RadioStyle} > input:first-child {
                    margin-left: 0;
                }
            }
        `,
    },
    checkboxGroupStyle: {
        mergeCss: true,
        labelProps: {
            size: 15,
            weight: "normal",
            css: css`
                letter-spacing: 0;
                line-height: 24px;
                flex-basis: 100%;
            `,
        },
        css: css`
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
            column-gap: 20px;

            ${CheckboxStyle} + ${CheckboxStyle} {
                margin-top: 0;
            }
        `,
    },
    alignToExistingAccountRadio: {
        css: css`
            margin-top: 12px;
            input {
                margin-left: 0;
            }
        `,
    },
    desiredRoleSelect: {
        cssOverrides: {
            formInputWrapper: css`
                width: 100%;
            `,
        },
    },
    adminNameEmailGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    cashAccountRadio: {
        css: css`
            input {
                margin-left: 0;
            }
        `,
    },
    stayInTouchRadio: {
        css: css`
            input {
                margin-left: 0;
            }
        `,
    },
    existingAccountsGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            &&& {
                margin-top: 20px;
            }
        `,
    },
    industryGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            &&& {
                margin-top: 20px;
            }
        `,
    },
    companyNameGridItem: {
        width: [10, 10, 6, 6, 6],
        css: css`
            padding-top: 23px;
        `,
    },
    companyNameInfoGridItem: {
        width: [2, 2, 6, 6, 6],
        css: css`
            align-items: center;
            padding-top: 23px;
            &&& {
                margin-top: 16px;
            }
        `,
    },
    addressGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    countriesGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    cityGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    stateGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    zipCodeGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    notesGridItem: {
        width: 12,
    },
    stayInTouchGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    emptyGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    uploadCreditApplicationSiteMessageGridItem: {
        width: 12,
    },
    uploadCreditApplicationGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            & > div {
                width: 100%;
            }
        `,
    },
    textArea: {
        css: css`
            height: 40px;
        `,
    },
    tooltip: {
        iconProps: {
            src: Question,
            color: "primary",
            size: 18,
        },
    },
};

const preventDefaultFormClick = (event: any) => {
    const target = event.target?.tagName.toLowerCase();
    const parentTarget = event.target?.parentElement?.tagName.toLowerCase();
    if (target === "button" || parentTarget === "button") {
        event.preventDefault();
        event.stopPropagation();
    }
};

const styles = createAccountApplyPurchasableFormStyles;
const CreateAccountApplyPurchasableForm: FC<OwnProps> = ({ accountData, accountForm }) => {
    if (!accountForm || !accountForm.state.fields.applyForPurchasable.value) {
        return null;
    }

    let fields: CreateForm<AccountFormState>["state"]["fields"] = {} as any;
    let isSubmitted;

    if (accountForm) {
        fields = accountForm.state?.fields;
        isSubmitted = accountForm.state?.isSubmitted;
    }

    const selectedCountry = accountData?.countries?.find(country => country.name === fields.country.value);
    return (
        <>
            <GridItem {...styles.havingAccountGridItem}>
                <RadioGroup
                    name="haveAccount"
                    label={siteMessage("Keller_Alignment_Selection_Message")}
                    value={fields.createAccountOption.value}
                    onChangeHandler={e => accountForm?.onFieldChange(fields.createAccountOption, e.currentTarget.value)}
                    error={isSubmitted && fields.createAccountOption.error}
                    {...fields.createAccountOption.attrs}
                    {...styles.radioGroupStyle}
                    horizontal={true}
                >
                    <Radio value={createAccountOptionValues.existingAccount} {...styles.alignToExistingAccountRadio}>
                        {translate(createAccountOptionValues.existingAccount)}
                    </Radio>
                    <Radio value={createAccountOptionValues.newAccount}>
                        {translate(createAccountOptionValues.newAccount)}
                    </Radio>
                </RadioGroup>
            </GridItem>
            <GridItem {...styles.desiredRoleGridItem}>
                <Select
                    value={fields.role.value}
                    onChange={e => accountForm?.onFieldChange(fields.role, e.currentTarget.value)}
                    error={isSubmitted && fields.role.error}
                    {...fields.role.attrs}
                    {...styles.desiredRoleSelect}
                >
                    <option value="">{translate("Select Option")}</option>
                    <option value="admin">{translate("Admin")}</option>
                    <option value="buyer">{translate("Buyer")}</option>
                </Select>
            </GridItem>
            {/* XNG- add tooltip to desired role  */}
            <GridItem {...styles.desiredRoleInfoGridItem}>
                <Tooltip {...styles.tooltip} text={siteMessage("Keller_DesiredRole_Tooltip").toString()} />
            </GridItem>
            {fields.role.value !== "" && (
                <>
                    {fields.role.value === "buyer" && (
                        <>
                            <GridItem {...styles.adminNameEmailGridItem}>
                                <TextField
                                    value={fields.adminName.value}
                                    onChange={e => accountForm?.onFieldChange(fields.adminName, e.currentTarget.value)}
                                    error={isSubmitted && fields.adminName.error}
                                    {...fields.adminName.attrs}
                                    {...styles.textField}
                                />
                            </GridItem>
                            <GridItem {...styles.adminNameEmailGridItem}>
                                <TextField
                                    value={fields.adminEmail.value}
                                    onChange={e => accountForm?.onFieldChange(fields.adminEmail, e.currentTarget.value)}
                                    error={isSubmitted && fields.adminEmail.error}
                                    {...fields.adminEmail.attrs}
                                    {...styles.textField}
                                />
                            </GridItem>
                        </>
                    )}
                    <GridItem {...styles.havingAccountGridItem}>
                        <RadioGroup
                            value={fields.accountType.value}
                            onChangeHandler={e => accountForm?.onFieldChange(fields.accountType, e.currentTarget.value)}
                            error={isSubmitted && fields.accountType.error}
                            horizontal={true}
                            {...fields.accountType.attrs}
                            {...styles.radioGroupStyle}
                        >
                            <Radio value="cash" {...styles.cashAccountRadio}>
                                {translate("Cash Account")}
                            </Radio>
                            <Radio value="credit">{translate("Credit Account")}</Radio>
                            <Radio value="both">{translate("Both")}</Radio>
                        </RadioGroup>
                    </GridItem>
                    {fields.createAccountOption.value === createAccountOptionValues.existingAccount && (
                        <>
                            <GridItem {...styles.existingAccountsGridItem}>
                                <TextField
                                    value={fields.customerAccounts.value}
                                    onChange={e =>
                                        accountForm?.onFieldChange(fields.customerAccounts, e.currentTarget.value)
                                    }
                                    error={isSubmitted && fields.customerAccounts.error}
                                    {...fields.customerAccounts.attrs}
                                    {...styles.textField}
                                />
                            </GridItem>
                        </>
                    )}
                    <GridItem {...styles.industryGridItem}>
                        <Select
                            value={fields.industry.value}
                            onChange={e => accountForm?.onFieldChange(fields.industry, e.currentTarget.value)}
                            error={isSubmitted && fields.industry.error}
                            {...fields.industry.attrs}
                            {...styles.desiredRoleSelect}
                        >
                            <option value="">{translate("Industry")}</option>
                            {accountData?.industries?.map(industry => (
                                <option value={industry} key={industry}>
                                    {industry}
                                </option>
                            ))}
                        </Select>
                    </GridItem>
                    <GridItem width={12}>
                        <GridContainer gap={22}>
                            <GridItem {...styles.companyNameGridItem}>
                                <TextField
                                    value={fields.companyName.value}
                                    onChange={e =>
                                        accountForm?.onFieldChange(fields.companyName, e.currentTarget.value)
                                    }
                                    error={isSubmitted && fields.companyName.error}
                                    {...fields.companyName.attrs}
                                    {...styles.textField}
                                />
                            </GridItem>
                            <GridItem {...styles.companyNameInfoGridItem}>
                                <Tooltip
                                    {...styles.tooltip}
                                    text={siteMessage("Keller_CreateAccount_Tooltip").toString()}
                                />
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                    <GridItem {...styles.addressGridItem}>
                        <TextField
                            value={fields.address1.value}
                            onChange={e => accountForm?.onFieldChange(fields.address1, e.currentTarget.value)}
                            error={isSubmitted && fields.address1.error}
                            {...fields.address1.attrs}
                            {...styles.textField}
                        />
                    </GridItem>
                    <GridItem {...styles.addressGridItem}>
                        <TextField
                            value={fields.address2.value}
                            onChange={e => accountForm?.onFieldChange(fields.address2, e.currentTarget.value)}
                            error={isSubmitted && fields.address2.error}
                            {...fields.address2.attrs}
                            {...styles.textField}
                        />
                    </GridItem>
                    <GridItem {...styles.countriesGridItem}>
                        <Select
                            value={fields.country.value}
                            onChange={e => accountForm?.onFieldChange(fields.country, e.currentTarget.value)}
                            error={isSubmitted && fields.country.error}
                            {...fields.country.attrs}
                        >
                            <option value="">{translate("Select Country")}</option>
                            {accountData?.countries?.map(country => (
                                <option key={country.id} value={country.name}>
                                    {country.name}
                                </option>
                            ))}
                        </Select>
                    </GridItem>
                    <GridItem {...styles.stateGridItem}>
                        <Select
                            value={fields.state.value}
                            onChange={e => accountForm?.onFieldChange(fields.state, e.currentTarget.value)}
                            error={isSubmitted && fields.state.error}
                            {...fields.state.attrs}
                        >
                            <option value="">{translate("Select State")}</option>
                            {selectedCountry?.states?.map(state => (
                                <option key={state.id} value={state.name}>
                                    {state.name}
                                </option>
                            ))}
                        </Select>
                    </GridItem>
                    <GridItem {...styles.cityGridItem}>
                        <TextField
                            value={fields.city.value}
                            onChange={e => accountForm?.onFieldChange(fields.city, e.currentTarget.value)}
                            error={isSubmitted && fields.city.error}
                            {...fields.city.attrs}
                            {...styles.textField}
                        />
                    </GridItem>
                    <GridItem {...styles.zipCodeGridItem}>
                        <TextField
                            value={fields.zip.value}
                            onChange={e => accountForm?.onFieldChange(fields.zip, e.currentTarget.value)}
                            error={isSubmitted && fields.zip.error}
                            {...fields.zip.attrs}
                            {...styles.textField}
                        />
                    </GridItem>
                    <GridItem {...styles.notesGridItem}>
                        <TextArea
                            value={fields.notes.value}
                            onChange={e => accountForm?.onFieldChange(fields.notes, e.currentTarget.value)}
                            error={isSubmitted && fields.notes.error}
                            {...fields.notes.attrs}
                            {...styles.textArea}
                        />
                    </GridItem>
                    <GridItem {...styles.stayInTouchGridItem}>
                        <CheckboxGroup
                            label={translate("Stay in touch with our offerings.")}
                            {...styles.checkboxGroupStyle}
                        >
                            <Checkbox
                                {...fields.stayInTouchByPrint.attrs}
                                checked={fields.stayInTouchByPrint.value}
                                onChange={(_, value) => accountForm?.onFieldChange(fields.stayInTouchByPrint, value)}
                            >
                                {translate("Print")}
                            </Checkbox>
                            <Checkbox
                                {...fields.stayInTouchByOnline.attrs}
                                checked={fields.stayInTouchByOnline.value}
                                onChange={(_, value) => accountForm?.onFieldChange(fields.stayInTouchByOnline, value)}
                            >
                                {translate("Online")}
                            </Checkbox>
                        </CheckboxGroup>
                    </GridItem>
                    <GridItem {...styles.emptyGridItem} />
                    <GridItem {...styles.uploadCreditApplicationSiteMessageGridItem}>
                        {siteMessage("Keller_application_link")}
                    </GridItem>
                    <GridItem {...styles.uploadCreditApplicationGridItem}>
                        <StyledWrapper onClick={preventDefaultFormClick}>
                            <FileUpload
                                fileName={fields.creditApplicationFile?.value?.name || ""}
                                accept=".pdf"
                                onFileChange={e =>
                                    accountForm?.onFieldChange(fields.creditApplicationFile, e.target.files?.[0])
                                }
                                error={isSubmitted && fields.creditApplicationFile.error}
                                labelPosition="top"
                                uid="kellerCreditApplicationUploadFileUpload"
                                buttonText={translate("Upload")}
                                hideButton={true}
                                includeButtonInInput={true}
                                {...fields.creditApplicationFile.attrs}
                            />
                        </StyledWrapper>
                    </GridItem>
                </>
            )}
        </>
    );
};

export default CreateAccountApplyPurchasableForm;
